/**
 * Mascot.jsx — reusable floating mascot with gold glow + hover speech bubble
 * ==========================================================================
 * <Mascot slug="king" role="hero" tip="Welcome home, high roller! 👑" />
 *
 * Props
 * -----
 *   slug   — one of: king | mafia_boss | astronaut | wizard |
 *                    viking_warrior | egyptian_princess
 *   size   — "sm" | "md" | "lg"        (default "md")
 *   role   — "hero" | "corner" | "inline"  (default "corner")
 *              hero   : large, drop shadow, centered, no fixed position
 *              corner : absolute right/top of parent, hides on <sm
 *              inline : renders inline next to a header on all breakpoints
 *   tip    — the string shown in the speech bubble on hover
 *   testId — data-testid override (defaults to `mascot-{slug}`)
 *
 * Notes
 * -----
 * - PNGs come from /images/mascots/{slug}.png — all with transparent bgs.
 * - Uses CSS-only floating (translateY ±5px, 2s ease-in-out infinite).
 * - Gold glow uses `filter: drop-shadow` so it hugs the alpha silhouette,
 *   not a rectangular bounding box.
 * - Speech bubble is pointer-events:none so it can't intercept clicks.
 * - Respects prefers-reduced-motion.
 */
import { useState } from "react";

const SIZES = {
  sm: { w: "w-16 sm:w-20",  h: "h-16 sm:h-20"  },
  md: { w: "w-24 sm:w-32",  h: "h-24 sm:h-32"  },
  lg: { w: "w-40 sm:w-56",  h: "h-40 sm:h-56"  },
  xl: { w: "w-56 sm:w-72 md:w-80", h: "h-56 sm:h-72 md:h-80" },
};

const IMG = {
  king:              "/images/mascots/king_mascot.png",
  mafia_boss:        "/images/mascots/mafia_boss.png",
  astronaut:         "/images/mascots/astronaut.png",
  wizard:            "/images/mascots/wizard.png",
  viking_warrior:    "/images/mascots/viking_warrior.png",
  egyptian_princess: "/images/mascots/egyptian_princess.png",
};

export default function Mascot({
  slug,
  size = "md",
  role = "corner",
  tip = "The reels are hot today! 👑",
  testId,
  className = "",
  onClick,
  ariaLabel,
}) {
  const [hovered, setHovered] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const src = IMG[slug];
  if (!src) return null;

  const dims = SIZES[size] || SIZES.md;
  const clickable = typeof onClick === "function";
  const wrapCls =
    role === "hero"
      ? "relative inline-flex items-center justify-center"
      : role === "inline"
        ? "relative inline-flex items-center justify-center align-middle"
        : "relative hidden sm:inline-flex items-center justify-center";

  const handleClick = (e) => {
    if (!clickable) return;
    setPulsing(true);
    setTimeout(() => setPulsing(false), 500);
    onClick(e);
  };

  const Wrapper = clickable ? "button" : "div";

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      aria-label={clickable ? (ariaLabel || `${slug} mascot button`) : undefined}
      className={`${wrapCls} ${clickable ? "cursor-pointer bg-transparent border-0 p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded-full active:scale-95 transition-transform" : ""} ${className}`}
      data-testid={testId || `mascot-${slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      <img
        src={src}
        alt={`${slug} mascot`}
        loading="lazy"
        decoding="async"
        className={`jr-mascot-float ${dims.w} ${dims.h} object-contain select-none ${
          hovered || clickable ? "jr-mascot-glow" : ""
        } ${pulsing ? "jr-mascot-pulse" : ""}`}
        draggable={false}
      />
      {tip && (
        <div
          className={`pointer-events-none absolute z-30 whitespace-nowrap px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-300 to-amber-500 text-slate-950 text-[11px] sm:text-xs font-black shadow-[0_6px_20px_-4px_rgba(251,191,36,0.6)] transition-all duration-200 top-full mt-3 left-1/2 -translate-x-1/2 sm:top-auto sm:-top-3 sm:mt-0 sm:left-full sm:translate-x-0 sm:ml-3 ${
            hovered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
          }`}
          style={{ fontFamily: "'Unbounded', sans-serif" }}
          data-testid={`mascot-${slug}-tip`}
        >
          <span
            className="absolute -top-1.5 left-1/2 -translate-x-1/2 sm:top-1/2 sm:-left-1.5 sm:-translate-y-1/2 sm:translate-x-0 w-3 h-3 rotate-45 bg-amber-300"
            aria-hidden
          />
          {tip}
        </div>
      )}
      <style>{`
        @keyframes jr-mascot-float {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-5px); }
        }
        .jr-mascot-float {
          animation: jr-mascot-float 2s ease-in-out infinite;
          will-change: transform;
          filter: drop-shadow(0 6px 16px rgba(0,0,0,0.55));
          transition: filter 0.35s ease;
        }
        .jr-mascot-glow {
          filter:
            drop-shadow(0 0 12px rgba(255,215,0,0.85))
            drop-shadow(0 0 24px rgba(255,215,0,0.55))
            drop-shadow(0 6px 16px rgba(0,0,0,0.55));
        }
        @keyframes jr-mascot-click-pulse {
          0%   { transform: translateY(0) scale(1);   }
          40%  { transform: translateY(-2px) scale(1.08); }
          100% { transform: translateY(0) scale(1);   }
        }
        .jr-mascot-pulse {
          animation: jr-mascot-float 2s ease-in-out infinite,
                     jr-mascot-click-pulse 0.5s ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .jr-mascot-float, .jr-mascot-pulse { animation: none; }
        }
      `}</style>
    </Wrapper>
  );
}
